import React from "react";
import { useTranslation } from "react-i18next";

// Material UI
import { Box, Typography } from "@mui/material";

// Custom Components
import Table from "../../components/Table/Table";
import AdministrationInfoLayout from "../../components/Layouts/AdministrationInfoLayout";
import LocationAverages from "./LocationAverages";
import GenericAvatar from "../../components/Icons/GenericAvatar";
import LocationEmployeesTable from "./LocationEmployeesTable";

const Datum = ({ label, value, ...props }) => (
  <Box display="flex" justifyContent="space-between">
    <Typography variant="body2" noWrap sx={{ pr: 1, flex: "0 1 40%" }}>
      {label}
    </Typography>
    <Typography
      variant="subtitle2"
      noWrap
      sx={{ pr: { xs: 0, md: 3 }, mr: { xs: "12.5%", lg: "25%" }, flex: 1 }}
      {...props}
    >
      {value ? value : "-"}
    </Typography>
  </Box>
);

const LocationInformation = ({ locationId, location, isLoading }) => {
  const { t } = useTranslation();

  const groupColumns = [
    {
      title: "Name",
      field: "name"
    }
  ];

  const locationAddress = location
    ? `${location.address1}${
        !!location.address2 ? `, ${location.address2}` : ""
      }`
    : "";

  const groupsTable = (
    <Table
      itemType="record"
      columns={groupColumns}
      data={location ? location.groups : []}
      hidePagination={true}
      isLoading={isLoading}
    />
  );

  const employeesTable = <LocationEmployeesTable locationId={locationId} />;

  return (
    <AdministrationInfoLayout
      name={location ? location.name : ""}
      image={<GenericAvatar icon="location" />}
      subtitle1={
        <Typography variant="subtitle1" color="textSecondary" sx={{ pb: 0.75 }}>
          {t("administration.units")}: {location ? location.units : ""} |{" "}
          {t("administration.employees")}:{" "}
          {location ? location.employeesCount : ""}
        </Typography>
      }
      compliance={location ? location.compliance : 0}
      completedTraining={location ? location.completedTraining : 0}
      trainingHours={location ? location.trainingHours : 0}
      averageScore={location ? location.averageScore : 0}
      averages={<LocationAverages locationId={locationId} />}
      infoSection={
        <>
          <Box display="flex" flexDirection="column" flex={1} minWidth={260}>
            <Datum
              label={t("administration.locationId")}
              value={location ? location.customerLocationId : ""}
              data-qa="location-location-id"
            />
            <Datum
              label={t("administration.address")}
              value={locationAddress}
              data-qa="location-address"
            />
            <Datum
              label={t("administration.city")}
              value={location ? location.city : ""}
              data-qa="location-city"
            />
            <Datum
              label={t("administration.state")}
              value={location ? location.state : ""}
              data-qa="location-state"
            />
            <Datum
              label={t("administration.zip")}
              value={location ? location.zipCode : ""}
              data-qa="location-zip"
            />
          </Box>
          <Box
            display="flex"
            flexDirection="column"
            flex={1}
            mb={3}
            minWidth={260}
          >
            <Datum
              label={t("administration.phone")}
              value={location ? location.phone : ""}
              data-qa="location-phone"
            />
            <Datum
              label={t("administration.fax")}
              value={location ? location.fax : ""}
              data-qa="location-fax"
            />
            <Datum
              label={t("administration.email")}
              value={location ? location.email : ""}
              data-qa="location-email"
            />
          </Box>
        </>
      }
      tables={[
        { header: t("administration.groupsHeader"), table: groupsTable },
        {
          header: t("administration.activeEmployeesHeader"),
          table: employeesTable
        }
      ]}
      isLoadingInfo={isLoading}
    />
  );
};

export default LocationInformation;
